<script setup>
import {
  computed,
  onMounted,
  useTemplateRef,
  ref,
  getCurrentInstance,
} from 'vue';
import Icon from 'next/icon/Icon.vue';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';
import { downloadFile } from '@chatwoot/utils';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';

const waveHeights = [20, 40, 60, 30, 80, 50, 70, 90, 40, 30, 80, 60, 100, 50, 30, 70, 90, 40, 60, 80, 50, 30, 90, 70, 40, 60, 80, 50, 30, 70];

const { attachment } = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
  showTranscribedText: {
    type: Boolean,
    default: true,
  },
});

defineOptions({
  inheritAttrs: false,
});

const timeStampURL = computed(() => {
  return timeStampAppendedURL(attachment.dataUrl);
});

const audioPlayer = useTemplateRef('audioPlayer');

const isPlaying = ref(false);
const isMuted = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const playbackSpeed = ref(1);

const { uid } = getCurrentInstance();

const onLoadedMetadata = () => {
  if (audioPlayer.value && !Number.isNaN(audioPlayer.value.duration)) {
    duration.value = audioPlayer.value.duration;
  }
};

const playbackSpeedLabel = computed(() => {
  return `${playbackSpeed.value}x`;
});

onMounted(() => {
  if (audioPlayer.value) {
    if (audioPlayer.value.duration && !Number.isNaN(audioPlayer.value.duration)) {
      duration.value = audioPlayer.value.duration;
    }
    audioPlayer.value.playbackRate = playbackSpeed.value;
  }
});

useEmitter('pause_playing_audio', currentPlayingId => {
  if (currentPlayingId !== uid && isPlaying.value) {
    try {
      audioPlayer.value.pause();
    } catch {
    }
    isPlaying.value = false;
  }
});

const formatTime = time => {
  if (!time || Number.isNaN(time) || time === Infinity) return '00:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const toggleMute = () => {
  audioPlayer.value.muted = !audioPlayer.value.muted;
  isMuted.value = audioPlayer.value.muted;
};

const onTimeUpdate = () => {
  currentTime.value = audioPlayer.value?.currentTime;
};

const seek = event => {
  const time = Number(event.target.value);
  audioPlayer.value.currentTime = time;
  currentTime.value = time;
};

const playOrPause = () => {
  if (isPlaying.value) {
    audioPlayer.value.pause();
    isPlaying.value = false;
  } else {
    if (duration.value > 0 && currentTime.value >= duration.value) {
      audioPlayer.value.currentTime = 0;
      currentTime.value = 0;
    }
    emitter.emit('pause_playing_audio', uid);
    audioPlayer.value.play();
    isPlaying.value = true;
  }
};

const onEnd = () => {
  isPlaying.value = false;
  playbackSpeed.value = 1;
  audioPlayer.value.playbackRate = 1;
};

const changePlaybackSpeed = () => {
  const speeds = [1, 1.5, 2];
  const currentIndex = speeds.indexOf(playbackSpeed.value);
  const nextIndex = (currentIndex + 1) % speeds.length;
  playbackSpeed.value = speeds[nextIndex];
  audioPlayer.value.playbackRate = playbackSpeed.value;
};

const downloadAudio = async () => {
  const { fileType, dataUrl, extension } = attachment;
  downloadFile({ url: dataUrl, type: fileType, extension });
};
</script>

<template>
  <audio
    ref="audioPlayer"
    :src="timeStampURL"
    controls
    class="hidden"
    playsinline
    preload="metadata"
    @loadedmetadata="onLoadedMetadata"
    @durationchange="onLoadedMetadata"
    @loadeddata="onLoadedMetadata"
    @canplay="onLoadedMetadata"
    @timeupdate="onTimeUpdate"
    @ended="onEnd"
  >
  </audio>
  <div
    v-bind="$attrs"
    class="w-full max-w-[280px] min-w-[200px] gap-2 flex flex-col items-stretch"
  >
    <div class="flex gap-2 w-full items-center justify-between">
      <button class="p-0 size-8 flex-shrink-0 grid place-content-center bg-transparent text-[#000000] hover:opacity-60 transition-opacity" @click="playOrPause">
        <Icon
          v-if="isPlaying"
          class="size-5"
          icon="i-teenyicons-pause-solid"
        />
        <Icon v-else class="size-5 ml-0.5" icon="i-teenyicons-play-solid" />
      </button>
      <div class="flex-col flex flex-1 min-w-[80px] max-w-[160px] justify-center">
        <div class="flex-1 items-center flex w-full relative h-6">
          <div class="absolute inset-0 flex items-center justify-between w-full overflow-hidden pointer-events-none px-1">
             <div v-for="(h, i) in waveHeights" :key="i" class="w-[3px] rounded-full transition-all duration-300"
                  :style="{
                    backgroundColor: 'currentColor',
                    opacity: (duration && (currentTime / duration) > (i / waveHeights.length)) ? '1' : '0.3',
                    height: h + '%',
                    animation: isPlaying ? `audio-wave-anim ${0.3 + (i%5)*0.1}s ease-in-out infinite alternate` : 'none',
                    animationDelay: `${i * 0.05}s`
                  }">
             </div>
          </div>
          <input
            type="range"
            min="0"
            :max="duration || 0"
            :value="currentTime"
            class="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
            @input="seek"
          />
        </div>
        <div class="flex justify-between items-center mt-1">
          <span class="tabular-nums text-[11px] text-[#000000]/70 font-mono">
            {{ formatTime(currentTime) }}
          </span>
          <span class="tabular-nums text-[11px] text-[#000000]/70 font-mono">
            {{ duration && duration !== Infinity ? formatTime(duration) : '00:00' }}
          </span>
        </div>
      </div>
      <div class="flex flex-none flex-col gap-1 items-center">
        <button
          class="border-0 h-6 px-2 grid place-content-center rounded-full"
          style="background-color: rgba(0,0,0,0.06);"
          @click="changePlaybackSpeed"
        >
          <span class="text-[10px] text-[#000000]/80 font-bold">
            {{ playbackSpeedLabel }}
          </span>
        </button>
      </div>
    </div>

    <div
      v-if="attachment.transcribedText && showTranscribedText"
      class="text-[#000000] p-2.5 text-sm bg-black/5 rounded-lg w-full break-words mt-2"
    >
      {{ attachment.transcribedText }}
    </div>
  </div>
</template>

<style>
@keyframes audio-wave-anim {
  0% { transform: scaleY(0.3); opacity: 0.8; }
  100% { transform: scaleY(1.3); opacity: 1; }
}
</style>
